/**
 * Reports Page
 * Analytics and statistics dashboard
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import Layout from '../components/Layout'
import { reportsService } from '../services/reports.service'
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'

export default function ReportsPage() {
  const [isExporting, setIsExporting] = useState(false)
  const { data: overallStats, isLoading: loadingOverall } = useQuery({
    queryKey: ['reports', 'overall'],
    queryFn: () => reportsService.getOverallStats(),
  })

  const { data: currencyStats, isLoading: loadingCurrency } = useQuery({
    queryKey: ['reports', 'currency'],
    queryFn: () => reportsService.getCurrencyStats(),
  })

  const { data: dailyEarnings, isLoading: loadingDaily } = useQuery({
    queryKey: ['reports', 'daily'],
    queryFn: () => reportsService.getDailyEarnings(30),
  })

  const { data: botPerformance, isLoading: loadingPerformance } = useQuery({
    queryKey: ['reports', 'performance'],
    queryFn: () => reportsService.getBotPerformance(),
  })

  const isLoading = loadingOverall || loadingCurrency || loadingDaily || loadingPerformance

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899']

  const handleExportCSV = async (type: 'earnings' | 'loans' | 'performance') => {
    setIsExporting(true)
    try {
      const blob = await reportsService.exportCSV(type, 30)
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `report_${type}_${Date.now()}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Failed to export CSV:', error)
      alert('導出 CSV 失敗，請稍後再試')
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <Layout>
      <div style={styles.container}>
        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>收益報表</h1>
            <p style={styles.subtitle}>分析您的放貸績效與收益統計</p>
          </div>
          <div style={styles.exportButtons}>
            <button
              style={styles.exportButton}
              onClick={() => handleExportCSV('earnings')}
              disabled={isExporting}
            >
              {isExporting ? '導出中...' : '📊 導出收益報表'}
            </button>
            <button
              style={styles.exportButton}
              onClick={() => handleExportCSV('loans')}
              disabled={isExporting}
            >
              {isExporting ? '導出中...' : '📋 導出貸款記錄'}
            </button>
            <button
              style={styles.exportButton}
              onClick={() => handleExportCSV('performance')}
              disabled={isExporting}
            >
              {isExporting ? '導出中...' : '📈 導出績效報表'}
            </button>
          </div>
        </div>

        {isLoading ? (
          <div style={styles.loading}>載入中...</div>
        ) : (
          <>
            {/* Overall Statistics */}
            <div style={styles.statsGrid}>
              <div style={styles.statCard}>
                <div style={styles.statIcon}>💰</div>
                <div style={styles.statContent}>
                  <p style={styles.statLabel}>總收益</p>
                  <p style={styles.statValue}>
                    ${(overallStats?.totalEarnings || 0).toFixed(2)}
                  </p>
                </div>
              </div>

              <div style={styles.statCard}>
                <div style={styles.statIcon}>📊</div>
                <div style={styles.statContent}>
                  <p style={styles.statLabel}>總放貸金額</p>
                  <p style={styles.statValue}>${(overallStats?.totalLent || 0).toFixed(2)}</p>
                </div>
              </div>

              <div style={styles.statCard}>
                <div style={styles.statIcon}>🤖</div>
                <div style={styles.statContent}>
                  <p style={styles.statLabel}>運行中 Bot</p>
                  <p style={styles.statValue}>
                    {overallStats?.activeBots || 0} / {overallStats?.totalBots || 0}
                  </p>
                </div>
              </div>

              <div style={styles.statCard}>
                <div style={styles.statIcon}>📈</div>
                <div style={styles.statContent}>
                  <p style={styles.statLabel}>平均年化收益</p>
                  <p style={styles.statValue}>{(overallStats?.averageAPR || 0).toFixed(2)}%</p>
                </div>
              </div>
            </div>

            {/* Charts Row 1: Daily Earnings Trend */}
            <div style={styles.chartSection}>
              <h2 style={styles.sectionTitle}>收益趨勢（近 30 天）</h2>
              <div style={styles.chartContainer}>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={dailyEarnings}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(value) => {
                        const date = new Date(value)
                        return `${date.getMonth() + 1}/${date.getDate()}`
                      }}
                    />
                    <YAxis />
                    <Tooltip
                      formatter={(value: number) => `$${value.toFixed(4)}`}
                      labelFormatter={(label) => `日期: ${label}`}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="earnings"
                      stroke="#3b82f6"
                      strokeWidth={2}
                      name="每日收益"
                      dot={{ fill: '#3b82f6', r: 3 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Charts Row 2: Currency Distribution */}
            <div style={styles.chartsRow}>
              <div style={styles.chartBox}>
                <h3 style={styles.chartTitle}>各幣種收益分布</h3>
                <div style={styles.chartContainer}>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={currencyStats}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={(entry) =>
                          `${entry.currency}: $${entry.totalEarned.toFixed(2)}`
                        }
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="totalEarned"
                      >
                        {currencyStats?.map((_entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value: number) => `$${value.toFixed(2)}`} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div style={styles.chartBox}>
                <h3 style={styles.chartTitle}>各幣種放貸金額</h3>
                <div style={styles.chartContainer}>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={currencyStats}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="currency" />
                      <YAxis />
                      <Tooltip formatter={(value: number) => `$${value.toFixed(2)}`} />
                      <Legend />
                      <Bar dataKey="totalLent" fill="#3b82f6" name="放貸金額" />
                      <Bar dataKey="totalEarned" fill="#10b981" name="收益" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* Currency Stats Table */}
            <div style={styles.tableSection}>
              <h2 style={styles.sectionTitle}>各幣種詳細統計</h2>
              <div style={styles.tableContainer}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>幣種</th>
                      <th style={styles.th}>總放貸</th>
                      <th style={styles.th}>總收益</th>
                      <th style={styles.th}>運行中 Bot</th>
                      <th style={styles.th}>平均日利率</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currencyStats?.map((stat) => (
                      <tr key={stat.currency} style={styles.tr}>
                        <td style={styles.td}>
                          <span style={styles.currencyBadge}>{stat.currency}</span>
                        </td>
                        <td style={styles.td}>${stat.totalLent.toFixed(2)}</td>
                        <td style={styles.td}>
                          <span style={styles.earningsBadge}>
                            ${stat.totalEarned.toFixed(4)}
                          </span>
                        </td>
                        <td style={styles.td}>{stat.activeBots}</td>
                        <td style={styles.td}>{stat.avgDailyRate.toFixed(4)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Bot Performance Table */}
            <div style={styles.tableSection}>
              <h2 style={styles.sectionTitle}>Bot 績效排名</h2>
              <div style={styles.tableContainer}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Bot 名稱</th>
                      <th style={styles.th}>幣種</th>
                      <th style={styles.th}>總收益</th>
                      <th style={styles.th}>總放貸</th>
                      <th style={styles.th}>ROI</th>
                      <th style={styles.th}>狀態</th>
                    </tr>
                  </thead>
                  <tbody>
                    {botPerformance?.map((bot, index) => (
                      <tr key={`${bot.name}-${index}`} style={styles.tr}>
                        <td style={styles.td}>{bot.name}</td>
                        <td style={styles.td}>
                          <span style={styles.currencyBadge}>{bot.currency}</span>
                        </td>
                        <td style={styles.td}>
                          <span style={styles.earningsBadge}>${bot.earned.toFixed(4)}</span>
                        </td>
                        <td style={styles.td}>${bot.lent.toFixed(2)}</td>
                        <td style={styles.td}>
                          <span
                            style={{
                              ...styles.roiBadge,
                              color: bot.roi > 0.1 ? '#10b981' : '#6b7280',
                            }}
                          >
                            {bot.roi.toFixed(4)}%
                          </span>
                        </td>
                        <td style={styles.td}>
                          <span
                            style={{
                              ...styles.statusBadge,
                              backgroundColor:
                                bot.status === 'active'
                                  ? '#d1fae5'
                                  : bot.status === 'paused'
                                    ? '#fef3c7'
                                    : '#f3f4f6',
                              color:
                                bot.status === 'active'
                                  ? '#059669'
                                  : bot.status === 'paused'
                                    ? '#d97706'
                                    : '#6b7280',
                            }}
                          >
                            {bot.status === 'active'
                              ? '運行中'
                              : bot.status === 'paused'
                                ? '暫停'
                                : '已停止'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Empty State */}
            {(!botPerformance || botPerformance.length === 0) && (
              <div style={styles.emptyState}>
                <div style={styles.emptyIcon}>📊</div>
                <h2 style={styles.emptyTitle}>尚無報表資料</h2>
                <p style={styles.emptyText}>建立並啟動您的第一個 Bot 以開始追蹤收益</p>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  )
}

const styles = {
  container: {
    padding: '40px',
  },
  header: {
    marginBottom: '30px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap' as const,
    gap: '20px',
  },
  title: {
    fontSize: '32px',
    fontWeight: '700',
    margin: '0 0 8px 0',
    color: '#111827',
  },
  subtitle: {
    fontSize: '16px',
    color: '#6b7280',
    margin: 0,
  },
  exportButtons: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap' as const,
  },
  exportButton: {
    padding: '10px 20px',
    backgroundColor: '#2563eb',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    whiteSpace: 'nowrap' as const,
  },
  loading: {
    textAlign: 'center' as const,
    padding: '60px 20px',
    fontSize: '18px',
    color: '#6b7280',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '20px',
    marginBottom: '40px',
  },
  statCard: {
    backgroundColor: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '12px',
    padding: '24px',
    display: 'flex',
    gap: '16px',
    alignItems: 'center',
  },
  statIcon: {
    fontSize: '32px',
  },
  statContent: {
    flex: 1,
  },
  statLabel: {
    fontSize: '14px',
    color: '#6b7280',
    margin: '0 0 8px 0',
  },
  statValue: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#111827',
    margin: 0,
  },
  chartSection: {
    backgroundColor: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '30px',
  },
  sectionTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#111827',
    marginBottom: '20px',
  },
  chartContainer: {
    width: '100%',
  },
  chartsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
    gap: '20px',
    marginBottom: '30px',
  },
  chartBox: {
    backgroundColor: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '12px',
    padding: '24px',
  },
  chartTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#111827',
    marginBottom: '16px',
  },
  tableSection: {
    backgroundColor: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '30px',
  },
  tableContainer: {
    overflowX: 'auto' as const,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
  },
  th: {
    textAlign: 'left' as const,
    padding: '12px 16px',
    backgroundColor: '#f9fafb',
    borderBottom: '2px solid #e5e7eb',
    fontSize: '14px',
    fontWeight: '600',
    color: '#374151',
  },
  tr: {
    borderBottom: '1px solid #e5e7eb',
  },
  td: {
    padding: '12px 16px',
    fontSize: '14px',
    color: '#111827',
  },
  currencyBadge: {
    display: 'inline-block',
    padding: '4px 12px',
    backgroundColor: '#dbeafe',
    color: '#1e40af',
    borderRadius: '12px',
    fontSize: '13px',
    fontWeight: '600',
  },
  earningsBadge: {
    color: '#059669',
    fontWeight: '600',
  },
  roiBadge: {
    fontWeight: '600',
  },
  statusBadge: {
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '13px',
    fontWeight: '600',
  },
  emptyState: {
    textAlign: 'center' as const,
    padding: '80px 20px',
  },
  emptyIcon: {
    fontSize: '64px',
    marginBottom: '20px',
  },
  emptyTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#111827',
    marginBottom: '12px',
  },
  emptyText: {
    fontSize: '16px',
    color: '#6b7280',
  },
} as const
